import React, { useEffect, useState, useContext, useRef } from "react";
import { Box, Typography, Chip, Card, CardContent, Paper } from "@mui/material";
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RecommendIcon from '@mui/icons-material/Recommend';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { createRoot } from 'react-dom/client';
import SubjectActionItem from './dashboard/SubjectActionItem';
import CategoryItem from './dashboard/CategoryItem';
import QuickOverview from './dashboard/QuickOverview';
import RecommendedActions from './dashboard/RecommendedActions';

const DashboardOverview = () => {
  const { selectedBusinesses, currencySymbol } = useContext(BusinessContext);
  const {
    aggregatedData,
    isAggregatedLoading,
    aggregatedError,
    fetchAggregatedDashboardData,
    view,
    timeframe,
    selectedDateRange,
    effectiveDateRange,
    sortOption,
    selectedSubject,
    setSelectedSubject,
    selectedAttribute,
    setSelectedAttribute,
    selectedSources,
    selectionSource,
    setSelectionSource,
    selectedCategory,
    setSelectedCategory,
    selectedDate,
    setSelectedDate
  } = useContext(ReviewsContext);
  
  const isLoading = isAggregatedLoading;

  const numSelected = selectedBusinesses?.length || 0;
  const singleSelectedBusiness = numSelected === 1 ? selectedBusinesses[0] : null;
  
  // Single consolidated useEffect for all data fetching
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      const businessIds = selectedBusinesses.map(b => b.id);
      // Fetch ALL data at once - this is more efficient than separate calls
      fetchAggregatedDashboardData(businessIds, timeframe, view, sortOption, selectedCategory, selectedSubject, selectedAttribute, effectiveDateRange, selectedSources, {
        updateSubjectsChart: true,
        updateAttributesChart: true,
        updateOverview: true
      });
    } else {
      // Clear data when no businesses selected
      // The context should handle this case
    }
  }, [
    selectedBusinesses, 
    timeframe, 
    view, 
    sortOption, 
    selectedCategory, 
    selectedSubject, 
    selectedAttribute, 
    effectiveDateRange,
    selectedSources,
    fetchAggregatedDashboardData
  ]);

  const categoryStats = aggregatedData?.categoryStats || { topCategories: [], totalPositiveRevenue: 0, totalNegativeRevenue: 0 };
  const recommendationStats = aggregatedData?.recommendationStats || { topSubjects: [], subjectAttributes: {} };
  const overallStats = aggregatedData?.overallStats || {
        positiveImpact: 0, negativeImpact: 0,
        positiveRevenueImpact: 0, negativeRevenueImpact: 0,
        averagePositiveRatingImpact: 0, averageNegativeRatingImpact: 0,
  };

  // Corrected logic for "top 3 subject:attribute pairs by attribute impact"
  const topActionablePairs = [];
  if (aggregatedData && recommendationStats.subjectAttributes && recommendationStats.topSubjects) {
    const subjectMap = new Map();
    recommendationStats.topSubjects.forEach(s => {
      subjectMap.set(`${s.name}|${s.isPositive ? 'positive' : 'negative'}`, s);
    });

    const allPairs = [];
    Object.entries(recommendationStats.subjectAttributes).forEach(([subjectKey, attributesArray]) => {
      const parentSubjectObject = subjectMap.get(subjectKey);
      if (parentSubjectObject && Array.isArray(attributesArray)) {
        attributesArray.forEach(attributeObject => {
          allPairs.push({
            subject: parentSubjectObject, // Full subject object
            attribute: attributeObject,   // Full attribute object
            // Unique key for React list rendering
            uniqueKey: `${parentSubjectObject.name}-${parentSubjectObject.isPositive ? 'positive' : 'negative'}-${attributeObject.name}` 
          });
        });
      }
    });

    allPairs.sort((pairA, pairB) => Math.abs(pairB.attribute.impact) - Math.abs(pairA.attribute.impact));
    topActionablePairs.push(...allPairs.slice(0, 25));
  }

  const dashboardRef = useRef(null);
  
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (selectedSubject || selectedAttribute || selectedCategory || selectedDate) {
        const isFilterTerm = event.target.dataset.filterTerm;
        
        const isCategoryBox = 
          event.target.dataset.categoryBox === 'true' || 
          !!event.target.closest('[data-category-box="true"]') || 
          !!event.target.closest('[data-filter-term="category"]');
        
        const isChartElement = 
          event.target.tagName === 'rect' || 
          event.target.tagName === 'path' ||
          event.target.closest('.MuiChartsLegend-root') || 
          event.target.closest('.MuiChartsAxis-root') ||
          !!event.target.closest('.MuiChart-root') ||
          !!event.target.closest('svg') ||
          event.target.classList?.contains('MuiBarElement-root') ||
          !!event.target.closest('.MuiBarElement-root') ||
          !!event.target.closest('[role="graphics-document"]');
        
        const isSidebarNavigation = 
          !!event.target.closest('.MuiDrawer-root') ||
          !!event.target.closest('.MuiDrawer-paper') ||
          !!event.target.closest('[role="presentation"]') ||
          event.target.closest('[data-testid="navigation-menu"]');

        const isExemptFromFiltering = 
          event.target.dataset.filterExempt === 'true' ||
          !!event.target.closest('[data-filter-exempt="true"]') ||
          !!event.target.closest('[data-chart-type="attribute-chart"]') ||
          !!event.target.closest('[data-chart-type="subject-chart"]') ||
          !!event.target.closest('[data-chart-type="timeline-chart"]') ||
          !!event.target.closest('[data-chart-type="comments-panel"]') ||
          !!event.target.closest('.MuiChartsBar-root') ||
          !!event.target.closest('.MuiBarChart-root');
        
        if (!isFilterTerm && !isChartElement && !isExemptFromFiltering && !isCategoryBox && !isSidebarNavigation) {
          setSelectedSubject(null);
          setSelectedAttribute(null);
          setSelectedCategory && setSelectedCategory(null);
          setSelectedDate && setSelectedDate(null);
        }
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [
    selectedSubject, 
    selectedAttribute, 
    selectedCategory, 
    selectedDate,
    setSelectedSubject, 
    setSelectedAttribute, 
    setSelectedCategory,
    setSelectedDate
  ]);
  
  const formatNumber = (num, isCurrency = false) => {
    if (num === undefined || num === null || Math.abs(num) < 0.001) return isCurrency ? `${currencySymbol}0` : '0★';
    
    const formatted = Math.abs(num).toFixed(3);
    return isCurrency 
      ? `${currencySymbol}${formatted}`
      : `${formatted}★`;
  };
  
  const formatImpact = (impact) => {
    if (impact === undefined || impact === null) return '0.000 ★';
    
    const absImpact = Math.abs(impact);
    
    if (absImpact === 0) return '0.000 ★';
    
    if (absImpact >= 0.001) {
      return `${absImpact.toFixed(3)} ★`;
    }
    
    const scientificNotation = absImpact.toExponential();
    
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1];
      const exponent = parseInt(matches[3], 10);
      
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return `${result} ★`;
    }
    
    return `${absImpact.toFixed(3)} ★`;
  };
  
  const formatCurrency = (value) => {
    if (value === undefined || value === null) {
      return `${currencySymbol}0`;
    }
    
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    let formattedValue = '';
    
    if (absValue >= 1000000) {
      formattedValue = `${currencySymbol}${(absValue / 1000000).toFixed(1)}m`;
    } else if (absValue >= 1000) {
      formattedValue = `${currencySymbol}${(absValue / 1000).toFixed(1)}k`;
    } else {
      formattedValue = `${currencySymbol}${absValue.toFixed(0)}`;
    }
    
    return isNegative ? `-${formattedValue}` : formattedValue;
  };
  
  const getTimeframeDays = () => {
    if (timeframe === 'all') return 'all time';
    return `last ${timeframe} days`;
  };
  
  const handleSubjectClick = (subject) => {
    const capitalizedSubject = subject;
    
    if (selectedSubject === capitalizedSubject) {
      setSelectedSubject(null);
      return;
    }
    
    setSelectedSubject(capitalizedSubject);
  };

  const shouldHighlightSubject = (subjectName) => {
    const capitalizedName = subjectName;
    
    if (capitalizedName === selectedSubject) {
      return true;
    }
    
    return false;
  };

  const handleAttributeClick = (attribute, parentSubject) => {
    const capitalizedSubject = parentSubject;
    const capitalizedAttribute = attribute;
    
    if (selectedAttribute === capitalizedAttribute && selectedSubject === capitalizedSubject) {
      setSelectedAttribute(null);
      return;
    }
    
    setSelectedSubject(capitalizedSubject);
    setSelectedAttribute(capitalizedAttribute);
  };

  const getSubjectImpactAction = (isPositive) => {
    return isPositive
      ? "Continue focus on "
      : "Improve ";
  };

  const getSubjectImpactContext = (isPositive) => {
    return "";
  };
  
  const shouldHighlightAttribute = (attributeName, parentSubjectName) => {
    const capitalizedSubject = parentSubjectName;
    const capitalizedAttribute = attributeName;
    
    if (capitalizedAttribute === selectedAttribute && capitalizedSubject === selectedSubject) {
      return true;
    }
    
    return false;
  };

  const isSubjectHighlighted = shouldHighlightSubject;
  const isAttributeHighlightingParent = (subjectName, attributeName) => 
    shouldHighlightAttribute(attributeName, subjectName);

  const handleCategoryClick = (categoryName) => {
    const formattedCategory = categoryName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    if (selectedCategory === formattedCategory) {
      setSelectedCategory(null);
      return;
    }
    
    setSelectedCategory(formattedCategory);
  };

  const isCategoryHighlighted = (categoryName) => {
    const formattedCategory = categoryName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    if (selectedCategory === formattedCategory) {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    if (isLoading || !aggregatedData) return;

    const quickOverviewContainer = document.getElementById('quick-overview-content');
    const recommendedActionsContainer = document.getElementById('recommended-actions-content');

    if (quickOverviewContainer && recommendedActionsContainer) {
      try {
        const quickOverviewContent = (
          <QuickOverview
            timeframe={timeframe}
            sortOption={sortOption}
            view={view}
            overallStats={overallStats}
            categoryStats={categoryStats}
            getTimeframeDays={getTimeframeDays}
            formatCurrency={formatCurrency}
            formatImpact={formatImpact}
            isCategoryHighlighted={isCategoryHighlighted}
            handleCategoryClick={handleCategoryClick}
            currencySymbol={currencySymbol}
            numSelectedBusinesses={numSelected}
          />
        );

        const recommendedActionsContent = (
          <RecommendedActions
            topActionablePairs={topActionablePairs}
            view={view}
            isSubjectHighlighted={isSubjectHighlighted}
            isAttributeHighlightingParent={isAttributeHighlightingParent}
            handleSubjectClick={handleSubjectClick}
            handleAttributeClick={handleAttributeClick}
            formatCurrency={formatCurrency}
            formatImpact={formatImpact}
            getSubjectImpactAction={getSubjectImpactAction}
            currencySymbol={currencySymbol}
            selectedBusiness={singleSelectedBusiness}
            isLoading={isLoading}
            selectedBusinesses={selectedBusinesses}
            selectedCategory={selectedCategory}
          />
        );

        if (!quickOverviewContainer._reactRoot) {
          quickOverviewContainer._reactRoot = createRoot(quickOverviewContainer);
        }
        if (!recommendedActionsContainer._reactRoot) {
          recommendedActionsContainer._reactRoot = createRoot(recommendedActionsContainer);
        }
        
        quickOverviewContainer._reactRoot.render(quickOverviewContent);
        recommendedActionsContainer._reactRoot.render(recommendedActionsContent);
      } catch (err) {
        console.error('Error rendering dashboard content:', err);
      }
    }
  }, [
    aggregatedData,
    categoryStats,
    recommendationStats,
    overallStats,
    view, 
    timeframe, 
    sortOption, 
    selectedSubject, 
    selectedAttribute,
    singleSelectedBusiness,
    isLoading,
    getTimeframeDays,
    formatCurrency,
    formatImpact,
    isCategoryHighlighted,
    handleCategoryClick,
    currencySymbol,
    isSubjectHighlighted,
    isAttributeHighlightingParent,
    handleSubjectClick,
    handleAttributeClick,
    getSubjectImpactAction,
    selectedBusinesses,
    selectedCategory,
    topActionablePairs,
  ]);

  return (
    <Box ref={dashboardRef} sx={{ p: 2, height: 'calc(100vh - SOME_HEADER_HEIGHT)', overflowY: 'auto' }}>
      {isLoading && <Typography>Loading dashboard data...</Typography>}
      {aggregatedError && <Typography color="error">Error loading dashboard: {aggregatedError}</Typography>}
      {!isLoading && !aggregatedError && aggregatedData && (
        <>
          <Paper id="quick-overview-content" sx={{ mb: 2, p: 2, boxShadow: 3 }}></Paper>
          <Paper id="recommended-actions-content" sx={{ p: 2, boxShadow: 3 }}></Paper>
        </>
      )}
      {!isLoading && !aggregatedError && !aggregatedData && selectedBusinesses && selectedBusinesses.length > 0 && (
        <Typography>No data available for the current selection.</Typography>
      )}
      {(!selectedBusinesses || selectedBusinesses.length === 0) && (
        <Typography>Please select one or more businesses to view the dashboard.</Typography>
      )}
    </Box>
  );
};

export default DashboardOverview; 